import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

const SERIF = "'PerfectNineties', serif";
const SANS  = "'MaisonNeueBook', sans-serif";
const VaiaLogo = ({ height = 42 }) => (
  <img
    src="/vaia-icon.png"
    alt="VAIA"
    style={{ height, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
    onError={(e) => { e.target.src = '/logo.png'; }}
  />
);

const Arrow = ({ color = '#2d2f24', size = 14 }) => (
  <svg width={size} height={size * 0.72} viewBox="0 0 14 10" fill="none">
    <path d="M1 5h12M8 1l5 4-5 4" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function LoginPage() {
  const { signInWithEmail } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userType = location.state?.userType || 'investor';
  const isAdvisor = userType === 'advisor';

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signInWithEmail(email, password);
      navigate('/consolidated');
    } catch (error) {
      toast.error(error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    fontFamily: SANS,
    width: '100%',
    boxSizing: 'border-box',
    padding: '11px 14px',
    fontSize: 15,
    color: '#2d2f24',
    backgroundColor: '#fff',
    border: '1px solid #d4d0c8',
    outline: 'none',
    transition: 'border-color 0.15s',
  };

  return (
    <div
      data-testid="login-page"
      style={{
        minHeight: '100vh',
        backgroundColor: '#f8f6f1',
        fontFamily: SANS,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <header style={{
        backgroundColor: '#fff',
        borderBottom: '1px solid rgba(0,0,0,0.07)',
        padding: '0 40px',
        height: 68,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Link to="/" style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <VaiaLogo height={38} />
          
        </Link>

        <Link to="/" style={{
          fontFamily: SANS, fontSize: 14, color: '#888',
          textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
        }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#2d2f24'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#888'}
        >
          ← Back to Home
        </Link>
      </header>

      {/* Main */}
      <main style={{
        flex: 1, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        padding: '60px 24px',
      }}>
        <div style={{ width: '100%', maxWidth: 420 }}>

          {/* Card */}
          <div style={{
            backgroundColor: '#fff',
            border: '1px solid #e4e0d8',
            padding: '48px 44px 40px',
          }}>
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <VaiaLogo height={44} />
              </div>
              <h2 style={{ fontFamily: SERIF, fontSize: 28, color: '#2a2a2a', fontWeight: 400, margin: '0 0 8px' }}>
                Family Office Platform
              </h2>
              <p style={{ fontFamily: SANS, fontSize: 14, color: '#888', margin: 0 }}>
                {isAdvisor ? 'Wealth Advisor Portal' : 'Investor Portal'}
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Email */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontFamily: SANS, fontSize: 13, color: '#555', display: 'block', marginBottom: 6 }}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="login-email-input"
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#2a2a2a'}
                  onBlur={(e)  => e.target.style.borderColor = '#d4d0c8'}
                />
              </div>

              {/* Password */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontFamily: SANS, fontSize: 13, color: '#555', display: 'block', marginBottom: 6 }}>
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    data-testid="login-password-input"
                    style={{ ...inputStyle, paddingRight: 42 }}
                    onFocus={(e) => e.target.style.borderColor = '#2a2a2a'}
                    onBlur={(e)  => e.target.style.borderColor = '#d4d0c8'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', padding: 0,
                    }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Remember + Forgot */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: SANS, fontSize: 13, color: '#888', cursor: 'pointer' }}>
                  <input type="checkbox" style={{ accentColor: '#2a2a2a' }} />
                  Remember me
                </label>
                <a href="#" style={{ fontFamily: SANS, fontSize: 13, color: '#888', textDecoration: 'none' }}
                  onMouseEnter={(e) => e.target.style.color = '#2d2f24'}
                  onMouseLeave={(e) => e.target.style.color = '#888'}
                >
                  Forgot password?
                </a>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                style={{
                  fontFamily: SANS,
                  width: '100%',
                  padding: '12px',
                  backgroundColor: loading ? '#ccc' : '#ffd879',
                  border: '0.5px solid #2a2a2a',
                  borderRadius: 50,
                  fontSize: 16,
                  color: '#2d2f24',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'background-color 0.15s',
                }}
              >
                {loading ? (
                  <>
                    <div style={{
                      width: 14, height: 14, border: '2px solid rgba(45,47,36,0.3)',
                      borderTopColor: '#2d2f24', borderRadius: '50%',
                      animation: 'spin 0.7s linear infinite',
                    }} />
                    Signing in…
                  </>
                ) : (
                  <>Sign In <Arrow /></>
                )}
              </button>
              {/* Switch portal */}
              <div style={{ marginTop: 24, textAlign: 'center' }}>
                <p style={{ fontFamily: SANS, fontSize: 13, color: '#888', margin: 0 }}>
                  {isAdvisor ? 'Not a wealth advisor?' : 'Managing client portfolios?'}{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/login', { state: { userType: isAdvisor ? 'investor' : 'advisor' } })}
                    style={{
                      fontFamily: SANS, background: 'none', border: 'none',
                      fontSize: 13, color: '#2d2f24', cursor: 'pointer',
                      textDecoration: 'underline', padding: 0,
                    }}
                  >
                    {isAdvisor ? 'Sign in as Investor' : 'Sign in as Advisor'}
                  </button>
                </p>
              </div>
            </form>
          </div>

          <p style={{ fontFamily: SANS, fontSize: 12, color: '#bbb', textAlign: 'center', marginTop: 24 }}>
            VAIA Investment Advisory LLP © 2026 · Secure & Private
          </p>
        </div>
      </main>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
